/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import moment from "helpers/moment";
import { Link, useNavigate } from 'react-router-dom';

/* Helpers, constants, custom methods, internal libraries */
import { useHasPermission } from "../../../../hooks/useHasPermission.js";
import { colors } from "../../../../constants/colors.js";
// import { PAYMENT_DETAILS_ROUTE } from "../../..//constants/routes.js";
import { useLocaleContext } from "../../../../contexts/localeContext.js";
import { useNotificationContext } from "../../../../contexts/notificationContext.js";

/* Components */
import UCGrid from "../../../ucgrid/index.js";
import UCButton from "../../../buttons/index.js";
import { ReactComponent as AssignIcon } from "../../../../resources/images/aa-users-plus.svg"
import { ReactComponent as PlusSign } from '../../../../resources/images/plus.svg';
import { ReactComponent as MatercardIcon } from "../../../../resources/images/aa-mastercard.svg"
import { ReactComponent as VisaIcon } from '../../../../resources/images/aa-visa.svg';
import { ReactComponent as AmexIcon } from '../../../../resources/images/aa-amex.svg';
import { ReactComponent as DiscoverIcon } from '../../../../resources/images/aa-discover.svg';
import { ReactComponent as DinnersClubIcon } from '../../../../resources/images/aa-dinnersclub.svg';
import { ReactComponent as UATPIcon } from '../../../../resources/images/aa-uatp.svg';
import { ReactComponent as JcbIcon } from '../../../../resources/images/aa-jcb.svg';
import { ReactComponent as UnionPayIcon } from '../../../../resources/images/aa-unionpay.svg';
import { ReactComponent as GenericPayIcon } from '../../../../resources/images/aa-genericpay.svg';
import { ReactComponent as RemovePaymentIcon } from '../../../../resources/images/aa-remove-payment.svg';
import { ReactComponent as ViasIcon } from '../../../../resources/images/cc-alaska.svg';
import PaymentNoPayments from "../../../paymentNoPayments/index.js"
import RemoveModal from "../../../modals/deleteActionModal/index.js"

/* Styles */
import styles from "./styles.js"

const PaymentsGrid = (props) => {
  const {gridOptions, setGridOptions, paymentResponse, displayShowAssignModal, deletePayment, addPayment, editPayment, viewPayment} = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();

  const globalLocale = useLocaleContext();
  const { checkPermission } = useHasPermission();
  const { setNotification } = useNotificationContext();

  const [selectedRow, setSelectedRow] = useState(null);
  const [showRemovePaymentModal, setShowRemovePaymentModal] = useState(false);

  const actionCallback = async (actionType, row) => {
    switch (actionType) {
      case "delete":
        setShowRemovePaymentModal(true)
        break;
      case "edit":
        await editPayment("paymentsGridSpinner.editAction", row?.fopUUID, row?.referenceId)
        break;
      case "view":
        await viewPayment("paymentsGridSpinner.viewAction", row?.fopUUID, row?.referenceId)
        break;
    }
  }

  let customProps = {};
  if (checkPermission("F_CREATE_PAYMENT")) {
    customProps.actionsComponent = (row) => {
      const cardIsExpired = new Date(moment(row.expirationDate, 'MM/YYYY').endOf('month').toDate()).getTime() < new Date().getTime()
      return <UCButton
          type="default"
          className={`${classes.assignBtn} ${cardIsExpired ? classes.disabledBtn : ""}`}
          text={<FormattedMessage id="paymentsGrid.assignButton"/>}
          icon={<AssignIcon />}
          disabled={cardIsExpired}
          onClick={() => displayShowAssignModal(row)}
          loadingStateStyling={classes.assingBtnLoadingState}
          spinner={{
            name: `assignTeamsButton${row?.fopUUID}`,
            size: 20,
            color: colors.darkerGrey
          }}
        />
      }
  }

  const getCardIcon = cardType => {
    switch(cardType) {
      case "CA":
        return <MatercardIcon/>
      case "VI":
        return <VisaIcon/>
      case "AX":
        return <AmexIcon/>
      case "DS":
        return <DiscoverIcon/>
      case "DC":
        return <DinnersClubIcon/>
      case "TP":
        return <UATPIcon/>
      case "JC":
        return <JcbIcon/>
      case "CU":
        return <UnionPayIcon/>
      case "VIAS":
      case "AS":
        return <ViasIcon/>
      default:
        return <GenericPayIcon/>
    }
  }

  const getCardText = ( cardType, cardNumberMasked) => {
    switch(cardType) {
      case "CA":
        return <FormattedMessage id="paymentsGrid.cardEndingMastercard" values={{cardNumber: cardNumberMasked}}/>
      case "VI":
        return <FormattedMessage id="paymentsGrid.cardEndingVisa" values={{cardNumber: cardNumberMasked}}/>
      case "AX":
        return <FormattedMessage id="paymentsGrid.cardEndingAmex" values={{cardNumber: cardNumberMasked}}/>
      case "DS":
        return <FormattedMessage id="paymentsGrid.cardEndingDiscover" values={{cardNumber: cardNumberMasked}}/>
      case "DC":
        return <FormattedMessage id="paymentsGrid.cardEndingDinnersClub" values={{cardNumber: cardNumberMasked}}/>
      case "TP":
        return <FormattedMessage id="paymentsGrid.cardEndingUniversalAirTravel" values={{cardNumber: cardNumberMasked}}/>
      case "JC":
        return <FormattedMessage id="paymentsGrid.cardEndingJCB" values={{cardNumber: cardNumberMasked}}/>
      case "CU":
        return <FormattedMessage id="paymentsGrid.cardEndingChinaUnionPay" values={{cardNumber: cardNumberMasked}}/>
      case "VIAS":
        return <FormattedMessage id="paymentsGrid.cardEndingAlaskaCard" values={{cardNumber: cardNumberMasked}}/>
      case "AS":
        return <FormattedMessage id="paymentsGrid.cardEndingAlaskaCommercialAccount" values={{cardNumber: cardNumberMasked}}/>
      default:
        break;
    }
  }

  const getExpiryDateText = expiryDate => {
    let momentDate =  moment(expiryDate, 'MM/YYYY')
    if (momentDate.isValid()) {
      let date = momentDate.endOf('month').toDate();
      let formattedExpiryDate = moment(date).locale(globalLocale).format("MMM YYYY")
      let now = new Date();
      let dateEntered = new Date(date).getTime();
      let sixMonthAfterNow = new Date(now).setMonth(now.getMonth() + 6);

      if(dateEntered < now.getTime()) {
        return <div className={classes.redExpiredDate}><FormattedMessage id="paymentsGrid.expiredDate" values={{expiryDate: formattedExpiryDate}}/></div>
      } else if(dateEntered < sixMonthAfterNow) {
        return <div className={classes.redExpiredDate}><FormattedMessage id="paymentsGrid.willExpireDate" values={{expiryDate: formattedExpiryDate}}/></div>
      } else {
        return <div className={classes.expiredDate}><FormattedMessage id="paymentsGrid.willExpireDate" values={{expiryDate: formattedExpiryDate}}/></div>
      }
    }
    return "";
  }

  const onRowClick = row => {}

  const deleteAction = async () => {
    const response = await deletePayment(selectedRow)
    if (response) {
      setNotification(
        "success",
        <FormattedMessage id="notification.deletePaymentSuccessTitle" />,
        <FormattedMessage id="notification.deletePaymentSuccessText" values={{name: getCardText(selectedRow?.cardType, selectedRow?.cardNumberMasked)}}/>
      )
      setShowRemovePaymentModal(false)
    }
  }

  return (
    <div className={classes.paymentsGrid}>
      <UCGrid
        gridName="paymentsGrid"
        data={ paymentResponse?.results }
        columns={ paymentResponse?.columns || [] }
        actions={ paymentResponse?.actions }
        actionsWidth={"60px"}
        handlePageChange={(page) => {setGridOptions({
          ...gridOptions,
          page
        })
        page > 1 ? navigate(`/app/payment?page=${page}`) : navigate(`/app/payment`)
        }}
        setSelectedRow={setSelectedRow}
        actionCallback={actionCallback}
        spinner="paymentsGridSpinner"
        customColumns={{
          lastUpdateOn: (row) => (
            <div className={classes.link}>{ moment(row?.lastUpdateOn).locale(globalLocale).format("D MMM YYYY") }</div>
          ),
          assignedName: (row) => (
            <div className={classes.assignedName}>
            { row?.assignedCount > 0 ?
              <>
                <span>{row?.assignedName}</span>
                {row?.assignedCount > 1 && 
                  <div>
                    <PlusSign />
                    <p>{row?.assignedCount - 1}</p>
                  </div>
                }
              </>
              : <span className={classes.unassigned}><FormattedMessage id="paymentsGrid.unassigned"/></span>
            }
            </div>
          ),
          parentName: (row) => (
            <Link /*to={`${row?.fopId}`}*/ className={`${classes.cardContainer} ${classes.link}`}>
              <div className={classes.cardIcon}>{getCardIcon(row.cardType)}</div>
              <div className={classes.cardDetails}>
                <div className={classes.nicknameText}>{row.nickname}</div>
                <div className={classes.cardNumberText}>{getCardText(row.cardType, row.cardNumberMasked)}</div>
                <div>{getExpiryDateText(row.expirationDate)}</div>
              </div>
            </Link>
          )
        }}
        columnCustomWidth={{
          assignedName: "317px !important",
          parentName: "285px !important"
        }}
        columnCustomProps={[
          // {
          //   columnName: "actions",
          //   allowOverflow: true,
          //   style: {
          //     display: 'flex',
          //     justifyContent: 'end',
          //     marginRight: "10px"
          //   }
          // }
        ]}
        hideEditAction={!checkPermission("F_EDIT_PAYMENT_DETAILS")}
        {...customProps}
        noDataComponent={ <PaymentNoPayments addPayment={addPayment}/> }
        onRowClick={onRowClick}
        // onRowClickRoute={row => PAYMENT_DETAILS_ROUTE.replace(":paymentId", row?.fopId)}
      />
      {
        paymentResponse?.actions && paymentResponse.actions.map((action,i) => {
          switch (action) {
            case "delete":
              return <React.Fragment key={`${action}-${i}`}>
                <RemoveModal
                  showModal={showRemovePaymentModal}
                  setShowModal={setShowRemovePaymentModal}
                  setSelectedRow={setSelectedRow}
                  modalIcon={ <RemovePaymentIcon /> }
                  modalTitle={ <div><FormattedMessage id="paymentsGrid.remove" />{getCardText(selectedRow?.cardType, selectedRow?.cardNumberMasked)}</div> }
                  modalDescription={<FormattedMessage id="paymentsGrid.removePaymentDescrition" />}
                  submitCallback={deleteAction}
                  submitText={<FormattedMessage id="paymentsGrid.removeAction" />}
                />
            </React.Fragment>
          }
        })
      }
    </div>
  )
}

export default PaymentsGrid;
